import { Reference } from '@ninetailed/experience.js';
import { ExperienceConfiguration } from '@ninetailed/experience.js-next';
import { useConfig } from 'config/use-config';
import { useCommon } from 'contexts/common';
import { INavigationElement } from 'global';
import { useIsExistingCustomer } from 'hooks/common/use-is-existing-customer';
import { INavigationData } from 'interfaces/navigation-data';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { parseExperiences } from 'utils/ninetailed';
import { NAVIGATION_ELEMENT_CONFIGS } from './data';
import { useNavigationExperiment } from './use-navigation-experiment';
import { useNavigationExperiments } from './use-navigation-experiments';
import { useProcessedNavigationElements } from './use-processed-navigation-elements';
import { createNavigationFromContentful, isNavigation } from './utils';
import { createReference } from './utils/empty-reference';
import { BREEDS_TITLE_ID, withSortedBreeds } from './utils/sort-breed-items';
import {
  cleanNavigationForExperiments,
  createExperimentData,
  findNavigationElement,
  getNavigationConfigKey,
} from './utils/experiment-helpers';

export interface ElementsByConfig {
  config: (typeof NAVIGATION_ELEMENT_CONFIGS)[number];
  element: INavigationElement | null;
  experimentData: {
    baseline: Reference | null;
    experiences: ExperienceConfiguration<Reference>[];
  };
}

/**
 * This hook returns the navigation data for the current shop based on the type of customer (NC or EC).
 */
const useNavigationData = (): INavigationData => {
  const { isEc: isExistingCustomer } = useIsExistingCustomer();
  const { navigation } = useCommon();
  const { commonPagePaths } = useConfig();
  const { formatMessage, locale } = useIntl();

  const localizedMessages = useMemo(
    () => ({
      dogTitle: formatMessage({ id: 'common:dogs' }),
      catTitle: formatMessage({ id: 'common:cats' }),
    }),
    [formatMessage]
  );

  // Clean navigation data for experiments with circular references to avoid errors
  const cleanedNavigation = cleanNavigationForExperiments(navigation);

  const navigationExperiment = useNavigationExperiment({
    baseline: createReference(cleanedNavigation),
    experiences: cleanedNavigation ? parseExperiences({ entry: cleanedNavigation }) : [],
    experimentName: cleanedNavigation?.fields?.title ?? 'main-navigation',
  });

  // Use the experimented navigation if available, otherwise fall back to original
  const navigationElements = useMemo(() => {
    const finalNavigation = navigationExperiment.variant ?? navigation;

    if (isNavigation(finalNavigation)) {
      return finalNavigation.fields.element ?? undefined;
    }

    return undefined;
  }, [navigationExperiment.variant, navigation]);

  // Create a map of elements by their configuration for easy access
  const elementsByConfig = useMemo(() => {
    if (!navigationElements) return new Map<string, ElementsByConfig>();

    const elementsMap = new Map<string, ElementsByConfig>();

    NAVIGATION_ELEMENT_CONFIGS.forEach((config) => {
      const element = findNavigationElement(navigationElements, config);
      const key = getNavigationConfigKey(config);
      elementsMap.set(key, {
        config,
        element,
        experimentData: createExperimentData(element),
      });
    });

    return elementsMap;
  }, [navigationElements]);

  // Use the custom hooks for better separation of concerns
  const experiments = useNavigationExperiments(elementsByConfig);
  const processedElements = useProcessedNavigationElements(navigationElements, experiments);

  // Generate basic fallback navigation to avoid errors
  const fallbackNavigationData = useMemo(
    () => ({
      mobile: { dog: [], cat: [] },
      desktop: [],
      footer: {
        items: [],
      },
      trustbar: {
        items: [],
      },
      companyInfo: {
        items: [],
      },
      header: {
        items: [],
      },
    }),
    []
  );

  // Final navigation data processing
  const navigationData = useMemo(() => {
    if (processedElements && Array.isArray(processedElements) && processedElements.length > 0) {
      const data = createNavigationFromContentful({
        elements: processedElements,
        isExistingCustomer,
        dogTitle: localizedMessages.dogTitle,
        catTitle: localizedMessages.catTitle,
        dogsHomePath: commonPagePaths.dogsHome,
        catsHomePath: commonPagePaths.catsHome,
        footer: navigation?.fields.footerMenu,
        companyInfo: navigation?.fields.companyInformation,
        header: navigation?.fields.headerSection,
      });

      const breedsTitle = formatMessage({ id: BREEDS_TITLE_ID });

      return {
        ...data,
        desktop: data.desktop.map((item) =>
          item.items ? { ...item, items: withSortedBreeds(item.items, breedsTitle, locale) } : item
        ),
        mobile: {
          ...data.mobile,
          dog: data.mobile.dog.map((group) => withSortedBreeds(group, breedsTitle, locale)),
        },
      };
    }

    return fallbackNavigationData;
  }, [
    processedElements,
    fallbackNavigationData,
    isExistingCustomer,
    localizedMessages.dogTitle,
    localizedMessages.catTitle,
    commonPagePaths.dogsHome,
    commonPagePaths.catsHome,
    navigation?.fields.footerMenu,
    navigation?.fields.headerSection,
    navigation?.fields.companyInformation,
    formatMessage,
    locale,
  ]);

  try {
    return navigationData;
  } catch (error) {
    console.error('Navigation data hook error:', error);
    // Eventually we could create a full fallback navigation data with all the elements
    // based on Shop ID and type of customer and store it in the codebase
    return fallbackNavigationData;
  }
};

export default useNavigationData;
